import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useService } from 'envoc-request';
import { createStrictContext } from 'envoc-strict-context';
import { PublicCameraDto, WebViewCameraService } from 'api';

const [ContextProvider, useCameraContext] = createStrictContext<{
  cameraList: PublicCameraDto[] | undefined;
  passwords: string[];
  loading: boolean;
  refetch: Function;
  error: any;
  tryPassword: (password: string) => Promise<PublicCameraDto[] | undefined>;
}>({
  name: 'Camera',
});

function encodePasswords(passwords: string[]) {
  return passwords.length > 0 ? btoa(JSON.stringify(passwords)) : undefined;
}

function CameraContextProvider({
  children,
  publicId,
}: React.PropsWithChildren<{ publicId: string }>) {
  const [passwords, setPasswords] = useState<string[]>([]);
  const { resp, loading, submitRequest, error } = useService(
    WebViewCameraService.getPublicCameras,
    { publicId, passwords: encodePasswords(passwords) },
    {
      autoExecute: false,
    }
  );
  const [cameraList, setCameraList] = useState<PublicCameraDto[]>();

  const tryPassword = useCallback(
    async (password: string) => {
      const newPasswords = [...new Set([...passwords, password])];
      const { result } = await WebViewCameraService.getPublicCameras({
        publicId,
        passwords: encodePasswords(newPasswords),
      });
      if (result && result.length > 0) {
        setCameraList(result);
        setPasswords(newPasswords);
      }
      const previouslyUnlockedCameraIds = cameraList
        ?.filter((camera) => camera.streamUrl)
        .map((camera) => camera.id);
      return (
        previouslyUnlockedCameraIds &&
        result &&
        result?.filter(
          (camera) =>
            camera.streamUrl && !previouslyUnlockedCameraIds.includes(camera.id)
        )
      );
    },
    [cameraList, passwords, publicId]
  );

  const refetch = useCallback(() => {
    submitRequest();
  }, [submitRequest]);

  useEffect(() => {
    if (resp?.result) {
      setCameraList(resp.result);
    }
  }, [resp?.result]);

  const prevPublicId = useRef('');
  useEffect(() => {
    if (publicId === '') {
      prevPublicId.current = publicId;
      setCameraList(undefined);
      setPasswords([]);
      return;
    }

    if (prevPublicId.current !== publicId) {
      prevPublicId.current = publicId;
      refetch();
      return;
    }
  }, [publicId, refetch]);

  useEffect(() => {
    if (loading) {
      setCameraList(undefined);
    }
  }, [loading]);

  const value = useMemo(() => {
    return {
      cameraList,
      passwords,
      loading,
      refetch,
      error,
      tryPassword,
    };
  }, [error, cameraList, loading, passwords, refetch, tryPassword]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
}

export { CameraContextProvider, useCameraContext };
